import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AccountBalanceWallet as WalletIcon } from '@mui/icons-material';

export interface RedemptionBalanceBreakdown {
  spendableQixCredits: number;
  pendingMaturityQixCredits: number;
  redeemableQixCredits: number;
  nonRedeemableSpendableQixCredits: number;
  pendingPayoutQixCredits: number;
  lifetimeEarned: number;
  lifetimeDeposited: number;
  lifetimeRedeemed: number;
  holdDays: number;
  minDisbursement: number;
  maxDisbursement: number;
}

export interface DisbursementHistoryItem {
  disbursementId: string;
  qixCreditsRedeemed: number;
  amountUsd: number;
  netDisbursed: number;
  status: string;
  method: string;
  requestedDate: string;
  completedDate?: string | null;
}

export interface DisbursementResult {
  success: boolean;
  message: string;
  disbursementId?: string;
  status?: string;
}

export interface RedeemCreditsTabProps {
  getBalanceBreakdown: () => Promise<RedemptionBalanceBreakdown>;
  requestDisbursement: (qixCreditsAmount: number) => Promise<DisbursementResult>;
  getDisbursementHistory: () => Promise<DisbursementHistoryItem[]>;
  getConnectStatus?: () => Promise<{
    isReadyForPayout: boolean;
    requireConnectSetup: boolean;
    payoutBlockers: string[];
    disabledReason?: string | null;
  }>;
  accountSettingsHref?: string;
}

const fmtCredits = (n: number) => `${n.toFixed(2)} Qix Credits`;

const CONNECT_BLOCKER_LABELS: Record<string, string> = {
  CONNECT_ONBOARDING_REQUIRED: 'Complete buyback settlement setup (Stripe Connect)',
  CONNECT_DETAILS_REQUIRED: 'Submit Connect bank and identity details',
  CONNECT_PAYOUTS_DISABLED: 'Connect settlement transfers must be enabled',
  W9_TIN_REQUIRED: 'W-9 / tax information required',
};

const RedeemCreditsTab: React.FC<RedeemCreditsTabProps> = ({
  getBalanceBreakdown,
  requestDisbursement,
  getDisbursementHistory,
  getConnectStatus,
  accountSettingsHref = '/profile?tab=account-settings',
}) => {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<RedemptionBalanceBreakdown | null>(null);
  const [history, setHistory] = useState<DisbursementHistoryItem[]>([]);
  const [connectBlockers, setConnectBlockers] = useState<string[]>([]);
  const [connectDisabledReason, setConnectDisabledReason] = useState<string | null>(null);
  const [payoutSetupRequired, setPayoutSetupRequired] = useState(false);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, h] = await Promise.all([getBalanceBreakdown(), getDisbursementHistory()]);
      setBalance(b);
      setHistory(h);
      if (getConnectStatus) {
        const connect = await getConnectStatus();
        setConnectBlockers(connect.payoutBlockers ?? []);
        setPayoutSetupRequired(connect.requireConnectSetup && !connect.isReadyForPayout);
        setConnectDisabledReason(connect.disabledReason ?? null);
      } else {
        setConnectBlockers([]);
        setPayoutSetupRequired(false);
        setConnectDisabledReason(null);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load redemption data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [getBalanceBreakdown, getDisbursementHistory, getConnectStatus]);

  const payoutBlocked = payoutSetupRequired && connectBlockers.length > 0;

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async () => {
    const parsed = parseFloat(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a valid Qix Credits amount.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await requestDisbursement(parsed);
      if (result.success) {
        setSuccess(result.message || 'Buyback request submitted.');
        setAmount('');
        await load();
      } else {
        setError(result.message || 'Request failed.');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Disbursement request failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !balance) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Balances are shown in <strong>Qix Credits</strong> — platform units, not cash. Eligible credits may be
        redeemed through Qixit&apos;s <strong>credit buyback program</strong>; U.S. dollars are paid only at ACH
        settlement when buyback is approved (1 Qix Credit = $1.00 USD unless otherwise disclosed).
      </Alert>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {payoutBlocked && (
        <Alert severity="warning">
          Complete buyback settlement setup (Stripe Connect + W-9) in{' '}
          <a href={accountSettingsHref}>Account Settings</a> before submitting a buyback request.
          {connectDisabledReason && (
            <>
              <br />
              {connectDisabledReason}
            </>
          )}
          {connectBlockers.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {connectBlockers.map((b) => (
                <li key={b}>{CONNECT_BLOCKER_LABELS[b] ?? b}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {balance && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WalletIcon color="primary" />
            Redemption balances
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography fontWeight="medium">Spendable in-platform</Typography>
              <Typography fontWeight="medium">{fmtCredits(balance.spendableQixCredits)}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
              Main wallet only. Vendor promotional credits are on the Vendor Credits tab and cannot be redeemed for cash.
            </Typography>
            <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Redeemable through buyback</Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {fmtCredits(balance.redeemableQixCredits)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Pending maturity ({balance.holdDays}-day hold)
                  </Typography>
                  <Typography variant="body2">{fmtCredits(balance.pendingMaturityQixCredits)}</Typography>
                </Box>
                {(balance.nonRedeemableSpendableQixCredits ?? 0) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '70%' }}>
                      Spendable only (not buyback-eligible)
                    </Typography>
                    <Typography variant="body2">{fmtCredits(balance.nonRedeemableSpendableQixCredits)}</Typography>
                  </Box>
                )}
              </Stack>
            </Box>
            {(balance.nonRedeemableSpendableQixCredits ?? 0) > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                Not buyback-eligible credits in your main wallet remain usable for postage, messages, and other
                in-platform spending. This is usually balance purchased with card or ACH, not platform-earned credits
                ({fmtCredits(balance.lifetimeDeposited)} lifetime purchased on record).
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography color="text.secondary">Pending buyback settlement</Typography>
              <Typography>{fmtCredits(balance.pendingPayoutQixCredits)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                Lifetime earned buyback pool · {fmtCredits(balance.lifetimeRedeemed)} already settled via buyback
              </Typography>
              <Typography variant="caption">
                {fmtCredits(balance.lifetimeEarned)} earned / {fmtCredits(balance.lifetimeDeposited)} purchased
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Request buyback redemption</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Minimum {balance?.minDisbursement?.toFixed(2) ?? '50.00'} Qix Credits per request. Buyback requests are
          reviewed before ACH settlement — credits are not cash until buyback completes.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            label="Qix Credits to redeem"
            type="number"
            size="small"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: balance?.minDisbursement ?? 50, step: '0.01' }}
            sx={{ minWidth: 220 }}
          />
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || payoutBlocked}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            Submit request
          </Button>
          {balance && balance.redeemableQixCredits > 0 && (
            <Button
              variant="outlined"
              onClick={() => setAmount(String(balance.redeemableQixCredits))}
            >
              Use full redeemable
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Buyback history</Typography>
        {history.length === 0 ? (
          <Typography color="text.secondary">No buyback requests yet.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Qix Credits</TableCell>
                  <TableCell align="right">USD (at settlement)</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.disbursementId}>
                    <TableCell>{new Date(row.requestedDate).toLocaleString()}</TableCell>
                    <TableCell align="right">{row.qixCreditsRedeemed.toFixed(2)}</TableCell>
                    <TableCell align="right">${row.amountUsd.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
};

export default RedeemCreditsTab;
